import * as XLSXStyle from "xlsx-js-style";
import { inyectarLogoEnExcel } from "./ExcelLogo";
import {
  headerStyle, textStyle, totalTextoStyle, totalMoneyStyle,
  celdaMoneda, celdaTexto, celdaNumero, celdaFecha, aNumero,
  direccionEmpresa, contactoEmpresa, nombreEmpresa,
} from "./ExcelEstilos";

/**
 * Excel de UNA requisición. Encabezado con el logo y los datos de la empresa,
 * de la requisición y del proyecto; abajo la tabla de partidas y el total
 * estimado.
 *
 * Se arma con el mismo `values` que consume `requisicionPdf`
 * (`requisiciones/verRequisicionesPdf` o su versión masiva), pero muestra más
 * que el PDF: la requisición no cotiza, así que el PDF no imprime precios,
 * mientras que aquí sí van el costo unitario, el importe estimado y el avance
 * de lo ya ordenado, que es lo que sirve para comprar.
 *
 * La requisición no tiene CFDI: por eso no reusa `ExcelDocumentoIndividual`,
 * que se arma todo desde un `dataSat`.
 */

const IMPORTANCIA = { 1: "Baja", 2: "Media", 3: "Alta" };
const ESTADOS_PARTIDA = { 0: "Cancelada", 1: "Pendiente", 2: "En proceso", 3: "Terminada" };

// A: Cantidad · B: Ordenada · C: Pendiente · D: Clave Int. · E: Clave SAT
// F: Unidad · G: Descripción · H: Observaciones · I: Fecha entrega
// J: Costo unitario · K: Importe estimado
const COLUMNAS = [
  { titulo: "Cantidad", ancho: 10 },
  { titulo: "Ordenada", ancho: 10 },
  { titulo: "Pendiente", ancho: 10 },
  { titulo: "Clave Int.", ancho: 16 },
  { titulo: "Clave SAT", ancho: 26 },
  { titulo: "Unidad", ancho: 12 },
  { titulo: "Descripcion", ancho: 42 },
  { titulo: "Observaciones", ancho: 28 },
  { titulo: "Fecha entrega", ancho: 14 },
  { titulo: "Costo unitario", ancho: 16 },
  { titulo: "Importe estimado", ancho: 18 },
];
const COL_ULTIMA = COLUMNAS.length - 1;
// El importe estimado cierra la tabla, y la etiqueta del total ocupa las dos
// columnas de su izquierda.
const COL_IMPORTE = COL_ULTIMA;
const COL_ETIQUETA = COL_IMPORTE - 2;

// El encabezado no lleva la retícula de la tabla, así que estos estilos van sin
// `border`.
const tituloEmpresaStyle = { font: { bold: true, sz: 14 }, alignment: { vertical: "center" } };
const etiquetaStyle = { font: { bold: true, sz: 10 }, alignment: { vertical: "center" } };
const valorStyle = { font: { sz: 10 }, alignment: { vertical: "center", wrapText: true } };
const valorFechaStyle = { font: { sz: 10 }, alignment: { horizontal: "left", vertical: "center" } };
const tituloDocumentoStyle = { font: { bold: true, sz: 12 }, alignment: { vertical: "center" } };
const descripcionStyle = { ...textStyle, alignment: { vertical: "center", wrapText: true } };
const totalDestacadoTextoStyle = { ...totalTextoStyle, fill: { fgColor: { rgb: "E8E8E8" } } };
const totalDestacadoMoneyStyle = { ...totalMoneyStyle, fill: { fgColor: { rgb: "E8E8E8" } } };

const vacia = () => ({ v: "" });
const filaVacia = () => COLUMNAS.map(() => vacia());

const nombreUsuario = (usuario) =>
  usuario
    ? [usuario.nombre, usuario.apellidoPaterno, usuario.apellidoMaterno].filter(Boolean).join(" ")
    : "";

const nombreProyecto = (proyecto) =>
  [proyecto?.nombre, proyecto?.descripcion].filter(Boolean).join(" - ");

/**
 * Renglón del encabezado: etiqueta+valor a la izquierda (A/C) y otro par a la
 * derecha (G/I). Cualquiera de los dos puede ir vacío.
 */
const filaEncabezado = ({ etiqueta, valor, valorCelda, valorStyle: vIzq, etiquetaDer, valorDer, valorDerCelda }) => {
  const fila = filaVacia();
  if (etiqueta !== undefined) fila[0] = celdaTexto(etiqueta, etiquetaStyle);
  if (valorCelda !== undefined) fila[2] = valorCelda;
  else if (valor !== undefined) fila[2] = celdaTexto(valor, vIzq || valorStyle);
  if (etiquetaDer !== undefined) fila[6] = celdaTexto(etiquetaDer, etiquetaStyle);
  if (valorDerCelda !== undefined) fila[9] = valorDerCelda;
  else if (valorDer !== undefined) fila[9] = celdaTexto(valorDer, valorStyle);
  return fila;
};

/**
 * @param {Object} values  la respuesta de `requisiciones/verRequisicionesPdf`
 *                         (o un elemento del masivo), tal cual la consume el PDF
 * @returns {Promise<Blob>} el .xlsx listo para descargar o meter al ZIP
 */
const construirExcelRequisicion = async (values) => {
  const empresa = values?.empresa || {};
  const requisicion = values?.data || {};
  const partidas = values?.insumos || [];

  const numero = [requisicion.serie, requisicion.numero]
    .filter((valor) => valor !== undefined && valor !== null && valor !== "")
    .join(" - ");

  const filas = [];
  const merges = [];
  // Une A:B (etiqueta), C:F (valor izquierdo), G:I (etiqueta derecha) y J:K
  // (valor derecho) del renglón que se acaba de empujar.
  const unirEncabezado = (indice) => {
    merges.push(
      { s: { r: indice, c: 0 }, e: { r: indice, c: 1 } },
      { s: { r: indice, c: 2 }, e: { r: indice, c: 5 } },
      { s: { r: indice, c: 6 }, e: { r: indice, c: 8 } },
      { s: { r: indice, c: 9 }, e: { r: indice, c: COL_ULTIMA } },
    );
  };
  const empujarEncabezado = (config) => {
    filas.push(filaEncabezado(config));
    unirEncabezado(filas.length - 1);
  };

  // ── Empresa (izquierda) y datos de la requisición (derecha) ───────────────
  empujarEncabezado({
    valor: nombreEmpresa(empresa), valorStyle: tituloEmpresaStyle,
    etiquetaDer: "Requisicion:", valorDer: numero,
  });
  // El número de la requisición en grande, como en el PDF.
  filas[filas.length - 1][9] = celdaTexto(numero, tituloDocumentoStyle);

  empujarEncabezado({
    valor: "R.F.C: " + (empresa.rfc || ""),
    etiquetaDer: "Fecha:", valorDerCelda: celdaFecha(requisicion.createdAt, valorFechaStyle),
  });
  empujarEncabezado({
    valor: direccionEmpresa(empresa),
    etiquetaDer: "Fecha de entrega:",
    valorDerCelda: celdaFecha(requisicion.fechaEntrega, valorFechaStyle),
  });
  empujarEncabezado({
    valor: contactoEmpresa(empresa),
    etiquetaDer: "Importancia:", valorDer: IMPORTANCIA[requisicion.importancia] || "",
  });

  filas.push(filaVacia());

  // ── Proyecto y responsable ────────────────────────────────────────────────
  empujarEncabezado({
    etiqueta: "Proyecto:", valor: nombreProyecto(values?.proyecto),
    etiquetaDer: "Solicito:", valorDer: nombreUsuario(values?.usuario),
  });
  if (requisicion.fechaSolicitud) {
    empujarEncabezado({
      etiqueta: "Fecha de solicitud:",
      valorCelda: celdaFecha(requisicion.fechaSolicitud, valorFechaStyle),
    });
  }

  filas.push(filaVacia());

  // ── Tabla de partidas ─────────────────────────────────────────────────────
  filas.push(COLUMNAS.map((columna) => celdaTexto(columna.titulo, headerStyle)));

  let totalEstimado = 0;
  for (const partida of partidas) {
    const cantidad = aNumero(partida.cantidad);
    const ordenada = aNumero(partida.cantidadOrdenada);
    const costo = aNumero(partida.costo);
    const importe = cantidad * costo;
    totalEstimado += importe;

    filas.push([
      celdaNumero(partida.cantidad),
      celdaNumero(partida.cantidadOrdenada || 0),
      celdaNumero(cantidad - ordenada),
      celdaTexto(partida.claveInt),
      celdaTexto(partida.claveSat),
      celdaTexto(partida.unidad),
      celdaTexto(partida.descripcion, descripcionStyle),
      celdaTexto(partida.observaciones, descripcionStyle),
      celdaFecha(partida.fechaEntrega),
      // Sin costo capturado la celda va vacía, pero conserva el formato de
      // moneda por si se teclea encima.
      celdaMoneda(partida.costo),
      celdaMoneda(partida.costo ? importe : ""),
    ]);
  }

  // ── Total estimado ────────────────────────────────────────────────────────
  const filaTotal = filaVacia();
  filaTotal[COL_ETIQUETA] = celdaTexto("TOTAL ESTIMADO:", totalDestacadoTextoStyle);
  filaTotal[COL_ETIQUETA + 1] = { v: "", s: totalDestacadoTextoStyle };
  filaTotal[COL_IMPORTE] = celdaMoneda(totalEstimado, totalDestacadoMoneyStyle);
  filas.push(filaTotal);
  merges.push({
    s: { r: filas.length - 1, c: COL_ETIQUETA },
    e: { r: filas.length - 1, c: COL_ETIQUETA + 1 },
  });

  // ── Observaciones ─────────────────────────────────────────────────────────
  if (requisicion.observaciones) {
    filas.push(filaVacia());
    filas.push([celdaTexto("Observaciones:", etiquetaStyle), ...filaVacia().slice(1)]);
    merges.push({ s: { r: filas.length - 1, c: 0 }, e: { r: filas.length - 1, c: COL_ULTIMA } });
    filas.push([celdaTexto(requisicion.observaciones, valorStyle), ...filaVacia().slice(1)]);
    merges.push({ s: { r: filas.length - 1, c: 0 }, e: { r: filas.length - 1, c: COL_ULTIMA } });
  }

  // ── Escritura ─────────────────────────────────────────────────────────────
  const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
  hoja["!cols"] = COLUMNAS.map((columna) => ({ wch: columna.ancho }));
  hoja["!merges"] = merges;

  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hoja, "Requisicion");

  const buffer = XLSXStyle.write(libro, { bookType: "xlsx", type: "array" });

  // El logo se inyecta a mano en el zip del .xlsx; si no se puede (sin logo, o
  // en un formato que Excel no acepta como Default), se baja igual sin imagen.
  const conLogo = await inyectarLogoEnExcel(buffer, empresa.logo, { fila: 0, columna: 0 });
  if (conLogo) return conLogo;

  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export { ESTADOS_PARTIDA, IMPORTANCIA, nombreProyecto, nombreUsuario };
export default construirExcelRequisicion;
